/**
 * Which failed requests ABP's error dialog must not see (GLOBAL_CONVERSATIONS_S4_TEST_RESULTS.md finding F1).
 *
 * `AbpHttpInterceptor.handleErrorResponse` opens its global dialog ("Resource not found!", "You are not authorized",
 * "An internal error occurred") only for an error whose body is a Blob, which is how every NSwag proxy asks for its
 * answer. The Global Conversations specs call a handful of calls background and silent: no click waits for them, the
 * caller's own error path (the 60 s breaker of SD2-30, a retry on the next view) is the whole reaction. For those
 * calls BackgroundCallErrorInterceptor, registered inside ABP's interceptor, swaps the Blob body for its text, so ABP
 * passes the error on without a dialog and the caller still gets its error.
 *
 * An endpoint is listed here only when EVERY call site in the app is such a background call. A 401 is never silenced:
 * ABP pairs its "not authenticated" message with the redirect to the login page in the same branch, after the refresh
 * token has failed, and the session is over for every call alike.
 *
 * No Angular imports, so the spec runs under plain jasmine.
 */

/** One app-service method, as `/api/services/app/<Service>/<Method>`. */
export interface SilencedEndpoint {
    readonly method: string;
    readonly path: string;
}

/** The background calls of Global Conversations S1-S4 (every call site, spec wording: S4_PROGRESS.md section 8). */
export const SILENCED_BACKGROUND_ENDPOINTS: readonly SilencedEndpoint[] = [
    // S4 D52 / §7.7: the overlay's once-per-open sync and the task modal's sync after GetTaskThread.
    { method: 'POST', path: '/api/services/app/EmailAutomation/SyncTaskThreadMessages' },
    // S2 SD2-06: the ambient auto-read 1 200 ms after the first All-scope page.
    { method: 'POST', path: '/api/services/app/Conversation/AutoReadAmbient' },
    // S2 SD2-11: the counts store (nav badge, Projects list, project page).
    { method: 'POST', path: '/api/services/app/Conversation/GetCounts' },
    // S1 FE-02: the task modal's 1.5 s comment dwell.
    { method: 'POST', path: '/api/services/app/Task/MarkCommentSeen' },
    // S3 SD3-07: the task modal's project-update dwell.
    { method: 'POST', path: '/api/services/app/ProjectComment/MarkProjectCommentSeen' },
];

/**
 * Pipeline Analytics (PA4, PIPELINE_ANALYTICS_PLAN.md): reads whose every failure the analytics window answers itself - Try
 * again in place, or a silent reload of the whole report on SNAPSHOT_EXPIRED (story section 10). Like the calls above, ABP's dialog must
 * not open; unlike them, the window must still READ the failure: its code is the Message of the server's UserFriendlyException,
 * and the NSwag proxies only parse an answer out of a Blob. So BackgroundCallErrorInterceptor hands these failures back as an
 * HttpResponse with the same status and a `text/plain` Blob body (`isCallerHandledCall`): ABP passes a non-JSON answer on
 * untouched, and the proxy's process method throws its usual SwaggerException carrying the JSON text. Every call site of these
 * endpoints is the analytics window.
 */
export const CALLER_HANDLED_ENDPOINTS: readonly SilencedEndpoint[] = [
    { method: 'GET', path: '/api/services/app/PipelineAnalytics/GetScoreboard' },
    { method: 'GET', path: '/api/services/app/PipelineAnalytics/GetSourceLeads' },
    { method: 'GET', path: '/api/services/app/PipelineAnalytics/GetLostReasonLeads' },
    { method: 'GET', path: '/api/services/app/PipelineAnalytics/GetPersonAvailability' },
];

/** The HTTP status ABP must keep handling itself (refresh token, then the login redirect). */
export const UNAUTHORIZED_STATUS = 401;

/** Scheme and host of an absolute URL (`https://host:port`). */
const ABSOLUTE_URL = /^[a-z][a-z0-9+.-]*:\/\/[^/]*/i;

/** Host of a protocol-relative URL (`//host`). */
const PROTOCOL_RELATIVE_URL = /^\/\/[^/]*/;

/**
 * The path of a request URL, lower case: scheme, host, query string and fragment dropped, repeated slashes collapsed
 * and trailing slashes removed.
 */
export function requestPath(url: string | null | undefined): string {
    let path: string = url || '';

    path = path.split('#')[0].split('?')[0];
    path = ABSOLUTE_URL.test(path) ? path.replace(ABSOLUTE_URL, '') : path.replace(PROTOCOL_RELATIVE_URL, '');
    path = path.replace(/\/{2,}/g, '/').replace(/\/+$/, '');
    return path.toLowerCase();
}

/**
 * Whether a request is one of the silenced background calls: same method (any case) and a path that is the endpoint's
 * path or ends with it (an API hosted under a base path).
 */
export function isSilencedBackgroundCall(
    method: string | null | undefined,
    url: string | null | undefined,
    endpoints: readonly SilencedEndpoint[] = SILENCED_BACKGROUND_ENDPOINTS
): boolean {
    const verb: string = (method || '').toUpperCase();
    const path: string = requestPath(url);

    if (!verb || !path) {
        return false;
    }
    return endpoints.some(
        (endpoint: SilencedEndpoint) =>
            endpoint.method.toUpperCase() === verb && path.endsWith(requestPath(endpoint.path))
    );
}

/**
 * Whether the error of a request must be hidden from ABP's dialog: a silenced background call, a status other than
 * 401, and a Blob body (any other body is passed on by ABP without a dialog already).
 */
export function silencesBackgroundError(
    method: string | null | undefined,
    url: string | null | undefined,
    status: number,
    bodyIsBlob: boolean
): boolean {
    return bodyIsBlob && status !== UNAUTHORIZED_STATUS && isSilencedBackgroundCall(method, url);
}

/** Whether a request is one of the caller-handled reads (CALLER_HANDLED_ENDPOINTS). */
export function isCallerHandledCall(method: string | null | undefined, url: string | null | undefined): boolean {
    return isSilencedBackgroundCall(method, url, CALLER_HANDLED_ENDPOINTS);
}

/**
 * Whether the failure of a request goes back to its caller as a `text/plain` answer: a caller-handled read, a status other than
 * 401 and a Blob body (any other failure is passed on as it is).
 */
export function handsFailureToCaller(
    method: string | null | undefined,
    url: string | null | undefined,
    status: number,
    bodyIsBlob: boolean
): boolean {
    return bodyIsBlob && status !== UNAUTHORIZED_STATUS && isCallerHandledCall(method, url);
}
